import { CheckCircleOutlineIcon, MotionPlayIcon } from '@/components/icons'
import { isVariableGroupSelectionBlocked, selectableGroupIds, VariableGroupsField } from '@/components/projects/workflows/shared'
import { ConnectorBadge, DynamicField } from '@/components/projects/workflows/shared/DynamicField'
import { buildDefaults, FIELD_HINT_CLASS, FIELD_LABEL_CLASS } from '@/components/projects/workflows/shared/dynamicFieldUtils'
import type { FormValues } from '@/components/projects/workflows/shared/dynamicFieldUtils'
import { SandboxImageSelector } from '@/components/sandbox-images'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { useConnectors, useContextSources, useRefreshVariableGroupTriggerOptions, useTriggerWorkflowDefinition, useVariableGroupTriggerOptions, useWorkflowTriggerSchema } from '@/hooks'
import { cn } from '@/lib/utils'
import { extractValidationMessages, getValidationErrorMessage, hasValidationError } from '@/lib/validationErrors'
import type { ContextSourceListItem } from '@/types'
import type { ClaudeRunOverrides } from '@/types/claude'
import type { CopilotRunOverrides } from '@/types/copilot'
import { AlertTriangle, ArrowRight, ChevronDown, Loader2, MessageSquare, Settings, X } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { ClaudeSettingsSection } from './ClaudeSettingsSection'
import { CopilotSettingsSection } from './CopilotSettingsSection'
import type { TriggerableDefinition } from './TriggerWorkflowFromEntityDialog'
import { isFieldVisible } from './chat/TriggerField'

const VARIABLE_GROUPS_FIELD = 'VariableGroups'

const PROVIDER_LABELS: Record<string, string> = {
  'claude-code': 'Claude',
  'copilot-cli': 'Copilot',
  'titan': 'Qubiz Titan',
}

interface TriggerWorkflowDialogProps {
  projectId: string
  definition: TriggerableDefinition | null
  open: boolean
  onOpenChange: (open: boolean) => void
  extraInputValues?: Record<string, unknown>
  onTriggerSuccess?: () => void
  linkedEntityId?: string
  linkedEntityIds?: string[]
  linkedEntityType?: 'WorkItem' | 'Intent' | 'Project'
  entityTitles?: Record<string, string>
  extraInputValuesPerEntity?: (entityId: string) => Record<string, unknown>
}

function hasAnyClaudeOverride(o: ClaudeRunOverrides): boolean {
  return !!(o.model || o.effort || (o.perNode && Object.keys(o.perNode).length > 0))
}

function hasAnyCopilotOverride(o: CopilotRunOverrides): boolean {
  return !!(o.model || o.effort || (o.perNode && Object.keys(o.perNode).length > 0))
}

export function TriggerWorkflowDialog({ projectId, definition, open, onOpenChange, extraInputValues, onTriggerSuccess, linkedEntityId, linkedEntityIds, linkedEntityType, entityTitles, extraInputValuesPerEntity }: TriggerWorkflowDialogProps) {
  const navigate = useNavigate()
  const [successRunId, setSuccessRunId] = useState<string | null>(null)
  const [successChatId, setSuccessChatId] = useState<string | null>(null)
  const [successInteractive, setSuccessInteractive] = useState(false)
  const [claudeOverrides, setClaudeOverrides] = useState<ClaudeRunOverrides>({})
  const [copilotOverrides, setCopilotOverrides] = useState<CopilotRunOverrides>({})
  const [bulkPending, setBulkPending] = useState(false)
  const isBulk = !!linkedEntityIds && linkedEntityIds.length > 0
  const [sandboxImageOverride, setSandboxImageOverride] = useState<string | null>(null)
  const [selectedProvider, setSelectedProvider] = useState<string | null>(null)
  const [showRerunWarning, setShowRerunWarning] = useState(false)
  const [pendingRerunData, setPendingRerunData] = useState<FormValues | null>(null)
  const [pendingBulkRerunIds, setPendingBulkRerunIds] = useState<string[] | null>(null)
  const triggerMutation = useTriggerWorkflowDefinition(projectId)
  const { data: schema, isLoading: schemaLoading } = useWorkflowTriggerSchema(projectId, open ? definition?.name ?? null : null)
  const { data: projectConnectors } = useConnectors(projectId)
  const repoConnectorTypes = useMemo(
    () => [...new Set((projectConnectors ?? []).filter(c => c.category !== 'AiProvider' && c.isEnabled !== false).map(c => c.type))],
    [projectConnectors],
  )
  const [advancedSettingsOpen, setAdvancedSettingsOpen] = useState(false)
  const [variableGroupIds, setVariableGroupIds] = useState<string[]>([])
  const refreshVariableGroupOptions = useRefreshVariableGroupTriggerOptions(projectId)
  const { data: variableGroupOptions, isLoading: variableGroupsLoading, error: variableGroupsError } = useVariableGroupTriggerOptions(
    projectId,
    open && definition ? { workflowName: definition.name } : null,
  )
  const variableGroupsBlocked = isVariableGroupSelectionBlocked(variableGroupOptions, variableGroupsLoading, variableGroupsError)

  const deferredNodesMissingPrompt = useMemo(() => {
    const usesClaudeShape = selectedProvider === 'claude-code' || selectedProvider === 'titan'
    const nodes = selectedProvider === 'copilot-cli'
      ? (schema?.copilotCliNodes ?? [])
      : usesClaudeShape
        ? (schema?.claudeCodeNodes ?? [])
        : []
    const overrides = selectedProvider === 'copilot-cli' ? copilotOverrides : claudeOverrides
    return nodes
      .filter(n => n.promptDeferred)
      .filter(n => {
        const rp = overrides.perNode?.[n.name]?.repoPrompt
        return !rp || !rp.connectorType || !rp.repository || !rp.filePath
      })
      .map(n => n.name)
  }, [selectedProvider, schema, claudeOverrides, copilotOverrides])

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormValues>({ mode: 'onBlur' })

  const hasContextPickerMultiSelect = useMemo(
    () => (schema?.fields ?? []).some(f => f.type === 'context-picker' && f.multiSelect),
    [schema?.fields],
  )
  const { data: contextDataForDefaults, isLoading: contextSourcesLoading } = useContextSources(
    projectId,
    { maxResultCount: 1000 },
    undefined,
    { enabled: open && hasContextPickerMultiSelect },
  )
  const allContextItems: ContextSourceListItem[] = useMemo(
    () => contextDataForDefaults?.items ?? [],
    [contextDataForDefaults],
  )

  const defaultsSessionRef = useRef<string | null>(null)

  useEffect(() => {
    if (!open) {
      defaultsSessionRef.current = null
      return
    }
    if (!schema?.fields) return
    if (hasContextPickerMultiSelect && contextSourcesLoading) return

    const sessionKey = definition?.name ?? ''
    if (defaultsSessionRef.current === sessionKey) return

    defaultsSessionRef.current = sessionKey
    reset(buildDefaults(schema.fields, allContextItems))
  }, [open, definition?.name, schema, hasContextPickerMultiSelect, contextSourcesLoading, allContextItems, reset])

  useEffect(() => {
    const providers = schema?.availableAgentProviders ?? []
    if (providers.length === 0) {
      setSelectedProvider(null)
      return
    }
    setSelectedProvider(prev => {
      if (prev !== null && providers.includes(prev)) return prev
      return providers[0]
    })
  }, [schema])

  const handleClose = (isOpen: boolean) => {
    if (!isOpen) {
      reset()
      setSuccessRunId(null)
      setSuccessChatId(null)
      setSuccessInteractive(false)
      setClaudeOverrides({})
      setCopilotOverrides({})
      setBulkPending(false)
      setSandboxImageOverride(null)
      setSelectedProvider(null)
      setShowRerunWarning(false)
      setPendingRerunData(null)
      setPendingBulkRerunIds(null)
      setAdvancedSettingsOpen(false)
      setVariableGroupIds([])
    }
    onOpenChange(isOpen)
  }

  if (!definition) return null

  const fields = (schema?.fields ?? []).filter(
    (f) => !extraInputValues || !(f.name in extraInputValues),
  )
  const connectors = schema?.connectors ?? []

  const doTrigger = async (data: FormValues, forceRerun = false, entityIds?: string[]) => {
    const visibleFieldNames = new Set(
      fields.filter((f) => isFieldVisible(f, data as Record<string, unknown>)).map((f) => f.name),
    )
    const filteredData = Object.fromEntries(
      Object.entries(data).filter(([key]) => visibleFieldNames.has(key)),
    )
    const basePayload = extraInputValues ? { ...filteredData, ...extraInputValues } : filteredData
    const claudeOv = selectedProvider === 'claude-code' && hasAnyClaudeOverride(claudeOverrides) ? claudeOverrides : undefined
    const copilotOv = selectedProvider === 'copilot-cli' && hasAnyCopilotOverride(copilotOverrides) ? copilotOverrides : undefined
    const selectedGroupIds = selectableGroupIds(variableGroupOptions, variableGroupIds)

    if (isBulk) {
      const idsToUse = entityIds ?? linkedEntityIds!
      setBulkPending(true)
      const results = await Promise.allSettled(
        idsToUse.map(id => {
          const perEntityValues = extraInputValuesPerEntity ? extraInputValuesPerEntity(id) : {}
          const itemPayload = { ...basePayload, ...perEntityValues }
          return triggerMutation.mutateAsync({
            name: definition.name,
            data: { inputValuesJson: JSON.stringify(itemPayload), linkedEntityId: id, linkedEntityType, claudeOverrides: claudeOv, copilotOverrides: copilotOv, sandboxImageId: sandboxImageOverride, forceRerun, agentProviderOverride: selectedProvider ?? undefined, variableGroupIds: selectedGroupIds },
          })
        })
      )
      setBulkPending(false)

      let succeeded = 0
      const failureTitles: string[] = []
      const activeRunIds: string[] = []
      let variableGroupError: string | null = null
      for (let i = 0; i < results.length; i++) {
        const result = results[i]
        const id = idsToUse[i]
        if (result.status === 'rejected') {
          if (hasValidationError(result.reason, 'ActiveRun')) {
            activeRunIds.push(id)
          } else if (hasValidationError(result.reason, VARIABLE_GROUPS_FIELD)) {
            variableGroupError ??= getValidationErrorMessage(result.reason, VARIABLE_GROUPS_FIELD)
          } else {
            failureTitles.push(entityTitles?.[id] ?? id)
          }
        } else {
          succeeded++
        }
      }

      if (variableGroupError) {
        toast.error(variableGroupError)
        void refreshVariableGroupOptions()
        if (succeeded === 0) return
      }

      failureTitles.forEach((title, index) => {
        setTimeout(() => toast.error(`Failed to trigger workflow for "${title}"`), index * 400)
      })

      if (activeRunIds.length > 0) {
        setPendingRerunData(data)
        setPendingBulkRerunIds(activeRunIds)
        setShowRerunWarning(true)
        return
      }

      if (succeeded > 0) {
        const label = succeeded === 1 ? 'item' : 'items'
        toast.success(`Workflow '${definition.displayName}' triggered for ${succeeded} ${label}.`)
      }

      reset()
      if (onTriggerSuccess) {
        handleClose(false)
        onTriggerSuccess()
      }
      return
    }

    const response = await triggerMutation.mutateAsync({
      name: definition.name,
      data: {
        inputValuesJson: JSON.stringify(basePayload),
        linkedEntityId,
        linkedEntityType,
        claudeOverrides: claudeOv,
        copilotOverrides: copilotOv,
        sandboxImageId: sandboxImageOverride,
        forceRerun,
        agentProviderOverride: selectedProvider ?? undefined,
        variableGroupIds: selectedGroupIds,
      },
    })
    reset()
    if (onTriggerSuccess) {
      handleClose(false)
      onTriggerSuccess()
    } else {
      setSuccessRunId(response.workflowRunId)
      setSuccessChatId(response.chatId)
      setSuccessInteractive(String((basePayload as Record<string, unknown>).interactiveMode) === 'true')
    }
  }

  const showTriggerErrorToasts = (err: unknown) => {
    if (hasValidationError(err, VARIABLE_GROUPS_FIELD)) void refreshVariableGroupOptions()

    const messages = extractValidationMessages(err)
    if (messages.length === 0) {
      toast.error('Failed to trigger workflow')
      return
    }
    messages.forEach((message, index) => {
      setTimeout(() => toast.error(message), index * 400)
    })
  }

  const handleFormSubmit = handleSubmit(async (data: FormValues) => {
    try {
      await doTrigger(data)
    } catch (err) {
      if (hasValidationError(err, 'ActiveRun')) {
        setPendingRerunData(data)
        setShowRerunWarning(true)
        return
      }
      showTriggerErrorToasts(err)
    }
  })

  const handleConfirmRerun = async () => {
    if (pendingBulkRerunIds && pendingRerunData) {
      try {
        await doTrigger(pendingRerunData, true, pendingBulkRerunIds)
        setShowRerunWarning(false)
        setPendingBulkRerunIds(null)
        setPendingRerunData(null)
      } catch (err) {
        showTriggerErrorToasts(err)
      }
      return
    }
    if (!pendingRerunData) return
    try {
      await doTrigger(pendingRerunData, true)
      setShowRerunWarning(false)
      setPendingRerunData(null)
    } catch (err) {
      showTriggerErrorToasts(err)
    }
  }

  return (
    <>
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent hideCloseButton className={cn('rounded-2xl p-0 shadow-elevated', successRunId ? 'max-w-[500px]' : 'max-w-[1000px]')}>
        {successRunId ? (
          <div className="flex flex-col items-center gap-2 pt-6 pr-8 pb-8 pl-8">
            <div className="self-end">
              <DialogClose className="rounded-sm text-foreground opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2" aria-label="Close">
                <X className="h-5 w-5" />
              </DialogClose>
            </div>
            <CheckCircleOutlineIcon className="h-10 w-10 min-h-10 min-w-10 text-success-foreground" />
            <DialogTitle className="mt-4 text-2xl font-bold leading-[1.3] tracking-[0.015em] text-foreground">
              Workflow triggered!
            </DialogTitle>
            <p className="text-sm text-foreground/72">
              Run ID: {successRunId}
            </p>
            {successInteractive && (
              <p className="mt-2 flex items-center gap-1.5 text-sm font-medium text-success-foreground">
                <MessageSquare className="h-4 w-4 min-h-4 min-w-4" />
                Interactive mode is on — join the chat to review phases
              </p>
            )}
            <div className="mt-8 flex w-full flex-col gap-3">
              {successInteractive ? (
                <>
                  <Button
                    onClick={() => {
                      handleClose(false)
                      navigate(`/projects/${projectId}/chat?chatId=${successChatId}`)
                    }}
                    disabled={!successChatId}
                    className="h-10 w-full rounded-lg text-sm font-bold leading-[1.6] tracking-[0.015em] px-4 gap-2"
                    data-testid="trigger-view-chat-button"
                  >
                    <MessageSquare className="h-5 w-5 min-h-5 min-w-5" />
                    View chat
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      handleClose(false)
                      navigate(`/projects/${projectId}/workflows/runs/${successRunId}`)
                    }}
                    className="h-10 w-full rounded-lg text-sm font-bold leading-[1.6] tracking-[0.015em] px-4 gap-2"
                    data-testid="trigger-view-run-button"
                  >
                    <MotionPlayIcon className="h-5 w-5 min-h-5 min-w-5" />
                    View run
                  </Button>
                </>
              ) : (
                <Button
                  onClick={() => {
                    handleClose(false)
                    navigate(`/projects/${projectId}/workflows/runs/${successRunId}`)
                  }}
                  className="h-10 w-full rounded-lg text-sm font-bold leading-[1.6] tracking-[0.015em] px-4 gap-2"
                  data-testid="trigger-view-run-button"
                >
                  <MotionPlayIcon className="h-5 w-5 min-h-5 min-w-5" />
                  View run
                  <ArrowRight className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        ) : (
          <form onSubmit={handleFormSubmit} className="flex flex-col max-h-[85vh] overflow-hidden">
            <div className="shrink-0 px-8 py-6 border-b space-y-1">
                <div className="flex items-center justify-between">
                  <DialogTitle className="text-2xl font-bold leading-[1.3] tracking-[0.015em] text-foreground">
                    Trigger: {schema?.displayName ?? definition.displayName}
                  </DialogTitle>
                  <DialogClose className="rounded-sm text-foreground opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2" aria-label="Close">
                    <X className="h-5 w-5" />
                  </DialogClose>
                </div>
                {schema?.description && (
                  <p className="text-sm text-foreground">{schema.description}</p>
                )}
              </div>
            <div className="min-h-0 px-8 py-6 space-y-6 overflow-y-auto overflow-x-hidden flex-1">
              {connectors.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {connectors.map((c) => (
                    <ConnectorBadge key={c.alias} connector={c} />
                  ))}
                </div>
              )}

              {(schema?.availableAgentProviders?.length ?? 0) > 1 && (
                <div className="flex gap-1 rounded-lg border border-border p-1 w-fit">
                  {schema!.availableAgentProviders!.map(provider => (
                    <button
                      key={provider}
                      type="button"
                      data-testid={`trigger-provider-${provider}`}
                      onClick={() => setSelectedProvider(provider)}
                      className={cn(
                        'rounded-md px-3 py-1 text-xs font-medium transition-colors',
                        selectedProvider === provider
                          ? 'bg-primary text-primary-foreground shadow-sm'
                          : 'text-muted-foreground hover:text-foreground'
                      )}
                    >
                      {PROVIDER_LABELS[provider] ?? provider}
                    </button>
                  ))}
                </div>
              )}

              {schemaLoading ? (
                <div className="flex items-center justify-center py-12" role="status" aria-live="polite">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  <span className="ml-2 text-sm text-muted-foreground">Loading trigger schema...</span>
                </div>
              ) : fields.length === 0 ? (
                <p className="text-sm text-foreground">This workflow has no trigger inputs. Click below to trigger it.</p>
              ) : (
                <div className="space-y-6">
                  {fields.map((field) => (
                    <DynamicField
                      key={field.name}
                      field={field}
                      projectId={projectId}
                      register={register}
                      errors={errors}
                      watch={watch}
                      setValue={setValue}
                    />
                  ))}
                </div>
              )}

              {!schemaLoading && (
                <VariableGroupsField
                  projectId={projectId}
                  options={variableGroupOptions}
                  isLoading={variableGroupsLoading}
                  error={variableGroupsError}
                  selectedIds={variableGroupIds}
                  onSelectedIdsChange={setVariableGroupIds}
                />
              )}

              <button
                type='button'
                onClick={() => setAdvancedSettingsOpen(o => !o)}
                aria-expanded={advancedSettingsOpen}
                aria-controls="advanced-settings-panel"
                data-testid="advanced-settings-toggle"
                className={cn(
                  'flex mb-0 w-full items-center text-muted-foreground justify-between bg-muted p-3 text-left',
                  advancedSettingsOpen ? 'rounded-t-lg' : 'rounded-lg',
                )}
              >
                <div className='flex gap-2 items-center'>
                  <Settings className='h-4 w-4'/>
                  Advanced Settings
                </div>
                <ChevronDown className={cn('h-4 w-4 transition-transform', advancedSettingsOpen && 'rotate-180')} />
              </button>

              <div
                id="advanced-settings-panel"
                className={cn('border rounded-b-lg p-4 flex flex-col gap-3 min-w-0', !advancedSettingsOpen && 'hidden')}
              >
                {selectedProvider === 'claude-code'
                  && (schema?.claudeCodeNodes?.length ?? 0) > 0
                  && schema?.baselineClaudeModel
                  && schema?.baselineClaudeEffort
                  && (
                  <ClaudeSettingsSection
                    baselineModel={schema.baselineClaudeModel}
                    baselineEffort={schema.baselineClaudeEffort}
                    claudeCodeNodes={schema.claudeCodeNodes!}
                    projectId={projectId}
                    connectorTypes={repoConnectorTypes}
                    value={claudeOverrides}
                    onChange={setClaudeOverrides}
                  />
                )}

                {selectedProvider === 'copilot-cli'
                  && (schema?.copilotCliNodes?.length ?? 0) > 0
                  && schema?.baselineCopilotModel
                  && schema?.baselineCopilotEffort
                  && (
                  <CopilotSettingsSection
                    baselineModel={schema.baselineCopilotModel}
                    baselineEffort={schema.baselineCopilotEffort}
                    copilotCliNodes={schema.copilotCliNodes!}
                    projectId={projectId}
                    connectorTypes={repoConnectorTypes}
                    value={copilotOverrides}
                    onChange={setCopilotOverrides}
                  />
                )}

                {selectedProvider === 'titan' && (
                  <div className="flex flex-col gap-1 max-w-sm" data-testid="trigger-titan-model">
                    <Label className={FIELD_LABEL_CLASS}>Model</Label>
                    <p className="text-sm font-medium">Titan</p>
                    <p className={FIELD_HINT_CLASS}>
                      {schema?.titanModel
                        ? `Set by the platform (${schema.titanModel}). Qubiz Titan has no model or effort to choose.`
                        : 'Set by the platform. Qubiz Titan has no model or effort to choose.'}
                    </p>
                  </div>
                )}

                {schema && (
                  <div className="flex flex-col gap-1 max-w-sm">
                    <Label className={FIELD_LABEL_CLASS}>Sandbox Image Override</Label>
                    <SandboxImageSelector
                      kind="Workflow"
                      value={sandboxImageOverride}
                      onChange={setSandboxImageOverride}
                      testId="trigger-sandbox-image-selector"
                    />
                    <p className={FIELD_HINT_CLASS}>
                      {sandboxImageOverride
                        ? 'This run only.'
                        : schema.resolvedSandboxImageName
                          ? `Inherits ${schema.resolvedSandboxImageName} from the definition.`
                          : 'Inherits from the definition.'}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {!schemaLoading && deferredNodesMissingPrompt.length > 0 && (
              <div className="shrink-0 px-8 pt-2" data-testid="deferred-prompt-warning">
                <p className="text-sm font-semibold text-amber-700" role="alert">
                  Select a prompt file for: {deferredNodesMissingPrompt.join(', ')}.
                </p>
              </div>
            )}

            {!schemaLoading && (
              <div className="shrink-0 flex justify-end gap-2 pt-4 pb-2 px-8 border-t border-[rgba(145,149,167,0.2)]">
                <Button
                  type="button"
                  variant="ghost"
                  className="h-10 rounded-lg text-sm font-bold leading-[1.6] tracking-[0.015em] px-4"
                  onClick={() => handleClose(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="h-10 rounded-lg text-sm font-bold leading-[1.6] tracking-[0.015em] px-4 gap-2"
                  disabled={bulkPending || triggerMutation.isPending || deferredNodesMissingPrompt.length > 0 || variableGroupsBlocked}
                  data-testid="trigger-submit-button"
                >
                  <MotionPlayIcon className="h-5 w-5 min-h-5 min-w-5" />
                  {bulkPending || triggerMutation.isPending ? 'Triggering...' : 'Trigger'}
                </Button>
              </div>
            )}
          </form>
        )}
      </DialogContent>
    </Dialog>

    <Dialog open={showRerunWarning} onOpenChange={(isOpen) => { if (!isOpen) { setShowRerunWarning(false); setPendingRerunData(null); setPendingBulkRerunIds(null) } }}>
      <DialogContent hideCloseButton onOpenAutoFocus={(e) => e.preventDefault()} className="max-w-[500px] gap-0 p-8 shadow-[0_0_12px_rgba(0,36,50,0.16)]">
        <DialogHeader className="flex flex-row items-center justify-between space-y-0">
          <DialogTitle className="text-2xl font-bold tracking-[0.015em] text-foreground">Re-run Workflow?</DialogTitle>
          <DialogClose aria-label="Close" className="rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2">
            <X className="h-5 w-5" />
            <span className="sr-only">Close</span>
          </DialogClose>
        </DialogHeader>

        <div className="mt-6 bg-warning rounded-lg p-4" data-testid="rerun-workflow-warning" role="alert">
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-5 w-5 text-warning-foreground shrink-0 mt-0.5" />
            <p className="text-base font-normal leading-[150%] tracking-[0.015em] text-warning-foreground">
              {pendingBulkRerunIds
                ? `${pendingBulkRerunIds.length} item(s) already have a running workflow. Force re-run those items?`
                : 'Are you sure you want to re-run the workflow? The details will be changed.'}
            </p>
          </div>
        </div>

        <div className="mt-8 flex justify-end gap-2">
          <Button
            variant="ghost"
            onClick={() => { setShowRerunWarning(false); setPendingRerunData(null); setPendingBulkRerunIds(null) }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmRerun}
            disabled={triggerMutation.isPending}
            className="h-10 rounded-lg text-sm font-bold leading-[1.6] tracking-[0.015em] px-4 gap-2"
            data-testid="rerun-workflow-confirm-button"
          >
            <MotionPlayIcon className="h-5 w-5 min-h-5 min-w-5" />
            {triggerMutation.isPending ? 'Triggering...' : 'Re-run Workflow'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
    </>
  )
}
